import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import type { TileChatRequest } from 'features/ask-yogi-anywhere/types/tile-chat-request'
import type { SkillContext } from 'features/ask-yogi/service'
import type { AgentAnnotationArtifact } from 'features/ask-yogi/types'
import type { HandedOffChart } from 'features/charts-v2/types/chart-handoff'
import type { ChartPageContext } from 'features/charts-v2/types/chart-page-context'
import type { PendingChartWrite } from 'features/charts-v2/types/chart-update'
import type {
  DashboardContextBuilder,
  DashboardFocusChip,
} from 'features/dashboards-v2-1/types/dashboard-page-context'
import type { PendingDashboardWrite } from 'features/dashboards-v2-1/types/dashboard-page-write'
import type { PageSkills, SavedResources } from 'features/project/page-skills'
import {
  AskYogiAnywhereContext,
  DashboardFocusContext,
} from 'features/project/partials/project-layout/contexts/use-ask-yogi-anywhere'
import { ChartPageRegistrationContext } from 'features/project/partials/project-layout/contexts/use-chart-page-registration'
import { ChartPageWriteContext } from 'features/project/partials/project-layout/contexts/use-chart-page-write'
import { DashboardPageRegistrationContext } from 'features/project/partials/project-layout/contexts/use-dashboard-page-registration'
import { DashboardPageWriteContext } from 'features/project/partials/project-layout/contexts/use-dashboard-page-write'
import {
  PageSkillsRegistrationContext,
  SavedResourcesContext,
} from 'features/project/partials/project-layout/contexts/use-page-skills'

export const AskYogiAnywhereProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAskYogiAnywhereOpen, setIsAskYogiAnywhereOpen] = useState(false)
  const [benchmarkingInsightUuid, setBenchmarkingInsightUuid] = useState<
    string | null
  >(null)
  const [benchmarkingInsightEntityName, setBenchmarkingInsightEntityName] =
    useState<string | null>(null)
  const [benchmarkingInitialQuestion, setBenchmarkingInitialQuestion] =
    useState<string | null>(null)
  const [wikiProjectUuid, setWikiProjectUuid] = useState<string | null>(null)
  const [chartPage, setChartPage] = useState<ChartPageContext | null>(null)
  const [isOnChartsPage, setIsOnChartsPageState] = useState(false)
  const [isOnEmptyDashboard, setIsOnEmptyDashboard] = useState(false)
  const [chartPageSession, setChartPageSession] = useState(0)
  const [handedOffChart, setHandedOffChart] = useState<HandedOffChart | null>(
    null,
  )

  // Wrapped so one visit to the charts page has one id. Stable (`[]`), so the
  // registration context it goes into keeps the constant identity that is the
  // entire reason that context was split out.
  //
  // **Bumped when the visit ENDS, not when one begins**, and the difference is
  // a live bug rather than a preference. `useRegisterChartContext` and
  // `useApplyAgentChart` run in the SAME commit, adjacent lines of the charts
  // container. Bumping on arrival only *schedules* the new value, so the apply
  // effect in that same commit still reads the old one — a write from the
  // previous visit compared equal and was applied, which is the case the
  // counter exists to stop. Unmount runs strictly before the next mount's
  // effects, so bumping on the way out has already taken effect by the time
  // anything reads it.
  const setIsOnChartsPage = useCallback((onChartsPage: boolean) => {
    setIsOnChartsPageState(onChartsPage)
    if (!onChartsPage) {
      setChartPageSession((previous) => previous + 1)
      // The chip should only show while the handed-off chart is still
      // actually on the page — once the visit ends, drop it.
      setHandedOffChart(null)
    }
  }, [])
  const [chartUpdate, setChartUpdate] = useState<PendingChartWrite | null>(null)
  const [dashboardWrite, setDashboardWrite] =
    useState<PendingDashboardWrite | null>(null)
  const [isAskYogiWorking, setIsAskYogiWorking] = useState(false)
  const [workingConversationId, setWorkingConversationId] = useState<
    string | null
  >(null)
  const [tileChatRequest, setTileChatRequest] =
    useState<TileChatRequest | null>(null)
  const [tileConversations, setTileConversations] = useState<
    Record<string, string>
  >({})

  const [pageSkills, setPageSkills] = useState<PageSkills | null>(null)
  const [savedResources, setSavedResources] = useState<SavedResources | null>(
    null,
  )
  const reportSavedResources = useCallback(
    (
      annotations: AgentAnnotationArtifact[],
      skillContext: SkillContext | undefined,
      pageWrites: Record<string, Record<string, unknown>> = {},
    ) => {
      setSavedResources((previous) => ({
        turn: (previous?.turn ?? 0) + 1,
        annotations,
        pageWrites,
        skillContext,
      }))
    },
    [],
  )

  const sendTileChat = useCallback((request: TileChatRequest) => {
    setTileChatRequest(request)
  }, [])

  const clearTileChatRequest = useCallback(() => {
    setTileChatRequest(null)
  }, [])

  const setTileConversation = useCallback(
    (tileId: string, conversationId: string) => {
      setTileConversations((previous) => ({
        ...previous,
        [tileId]: conversationId,
      }))
    },
    [],
  )

  // Stable, so the charts page's apply effect does not re-run every time this
  // provider re-renders — which it does on every widget open and close.
  const clearChartUpdate = useCallback(() => {
    setChartUpdate(null)
  }, [])
  // Stable for the same reason, for the dashboard's apply effect.
  const clearDashboardWrite = useCallback(() => {
    setDashboardWrite(null)
  }, [])

  // The 2.1 dashboard on screen registers how to describe itself; the widget
  // runs it when a message is sent. A ref, never state: the builder is a
  // function (state would call it as an updater) and nothing should
  // re-render when a page registers.
  const dashboardContextBuilderRef = useRef<DashboardContextBuilder | null>(
    null,
  )
  const registerDashboardContextBuilder = useCallback(
    (builder: DashboardContextBuilder) => {
      dashboardContextBuilderRef.current = builder
    },
    [],
  )
  const unregisterDashboardContextBuilder = useCallback(
    (builder: DashboardContextBuilder) => {
      if (dashboardContextBuilderRef.current === builder) {
        dashboardContextBuilderRef.current = null
      }
    },
    [],
  )
  const getDashboardContextBuilder = useCallback(
    () => dashboardContextBuilderRef.current,
    [],
  )
  const [dashboardFocus, setDashboardFocus] =
    useState<DashboardFocusChip | null>(null)
  const clearDashboardFocus = useCallback((chip: DashboardFocusChip | null) => {
    setDashboardFocus((previous) => (previous === chip ? null : previous))
  }, [])

  const toggleAskYogiAnywhere = useCallback(() => {
    setIsAskYogiAnywhereOpen((prev) => !prev)
  }, [])

  const openForBenchmarking = useCallback(
    (insightUuid: string, entityName?: string, initialQuestion?: string) => {
      setBenchmarkingInsightUuid(insightUuid)
      setBenchmarkingInsightEntityName(entityName ?? null)
      setBenchmarkingInitialQuestion(initialQuestion ?? null)
      setIsAskYogiAnywhereOpen(true)
    },
    [],
  )

  const exitBenchmarkingMode = useCallback(() => {
    setBenchmarkingInsightUuid(null)
    setBenchmarkingInsightEntityName(null)
    setBenchmarkingInitialQuestion(null)
    setIsAskYogiAnywhereOpen(false)
  }, [])

  const openForWiki = useCallback((projectUuid: string) => {
    setWikiProjectUuid(projectUuid)
    setIsAskYogiAnywhereOpen(true)
  }, [])

  const exitWikiMode = useCallback(() => {
    setWikiProjectUuid(null)
    setIsAskYogiAnywhereOpen(false)
  }, [])

  const value = useMemo(
    () => ({
      isAskYogiAnywhereOpen,
      setIsAskYogiAnywhereOpen,
      toggleAskYogiAnywhere,
      benchmarkingInsightUuid,
      benchmarkingInsightEntityName,
      benchmarkingInitialQuestion,
      openForBenchmarking,
      exitBenchmarkingMode,
      wikiProjectUuid,
      openForWiki,
      exitWikiMode,
      chartPage,
      setChartPage,
      chartPageSession,
      isOnChartsPage,
      setIsOnChartsPage,
      isOnEmptyDashboard,
      setIsOnEmptyDashboard,
      handedOffChart,
      chartUpdate,
      setChartUpdate,
      clearChartUpdate,
      setDashboardWrite,
      isAskYogiWorking,
      setIsAskYogiWorking,
      workingConversationId,
      setWorkingConversationId,
      tileChatRequest,
      sendTileChat,
      clearTileChatRequest,
      tileConversations,
      setTileConversation,
      getDashboardContextBuilder,
      pageSkills,
      reportSavedResources,
    }),
    [
      isAskYogiAnywhereOpen,
      toggleAskYogiAnywhere,
      benchmarkingInsightUuid,
      benchmarkingInsightEntityName,
      benchmarkingInitialQuestion,
      openForBenchmarking,
      exitBenchmarkingMode,
      wikiProjectUuid,
      openForWiki,
      exitWikiMode,
      chartPage,
      chartPageSession,
      isOnChartsPage,
      setIsOnChartsPage,
      isOnEmptyDashboard,
      handedOffChart,
      chartUpdate,
      clearChartUpdate,
      isAskYogiWorking,
      workingConversationId,
      tileChatRequest,
      sendTileChat,
      clearTileChatRequest,
      tileConversations,
      setTileConversation,
      getDashboardContextBuilder,
      pageSkills,
      reportSavedResources,
    ],
  )

  // The charts page pushes into these and reads nothing else, so they get
  // a value that never changes rather than one that re-memoises whenever the
  // widget opens. All three are `useState` setters and therefore already
  // stable; the empty dependency list is what makes that visible to consumers.
  const registration = useMemo(
    () => ({ setChartPage, setIsOnChartsPage, setHandedOffChart }),
    [setChartPage, setIsOnChartsPage, setHandedOffChart],
  )

  // The write-back half, on its own context for the same reason: it changes
  // only when a chart is written, and the page must re-render exactly then —
  // not on every widget toggle, which reading the wide value would cost it.
  // Stable, like `registration`: a page that registers skills reads nothing.
  const pageSkillsRegistration = useMemo(
    () => ({ setPageSkills, setIsAskYogiAnywhereOpen }),
    [setPageSkills, setIsAskYogiAnywhereOpen],
  )

  const chartWrite = useMemo(
    () => ({ chartUpdate, clearChartUpdate, chartPageSession }),
    [chartUpdate, clearChartUpdate, chartPageSession],
  )
  const dashboardPageWrite = useMemo(
    () => ({ dashboardWrite, clearDashboardWrite }),
    [dashboardWrite, clearDashboardWrite],
  )

  // Never changes after mount, for the same reason as `registration`.
  const dashboardRegistration = useMemo(
    () => ({
      registerDashboardContextBuilder,
      unregisterDashboardContextBuilder,
      publishDashboardFocus: setDashboardFocus,
      clearDashboardFocus,
    }),
    [
      registerDashboardContextBuilder,
      unregisterDashboardContextBuilder,
      clearDashboardFocus,
    ],
  )

  return (
    <AskYogiAnywhereContext.Provider value={value}>
      <ChartPageRegistrationContext.Provider value={registration}>
        <DashboardPageRegistrationContext.Provider
          value={dashboardRegistration}
        >
          <ChartPageWriteContext.Provider value={chartWrite}>
            <DashboardPageWriteContext.Provider value={dashboardPageWrite}>
              <DashboardFocusContext.Provider value={dashboardFocus}>
                <PageSkillsRegistrationContext.Provider
                  value={pageSkillsRegistration}
                >
                  <SavedResourcesContext.Provider value={savedResources}>
                    {children}
                  </SavedResourcesContext.Provider>
                </PageSkillsRegistrationContext.Provider>
              </DashboardFocusContext.Provider>
            </DashboardPageWriteContext.Provider>
          </ChartPageWriteContext.Provider>
        </DashboardPageRegistrationContext.Provider>
      </ChartPageRegistrationContext.Provider>
    </AskYogiAnywhereContext.Provider>
  )
}
